import { useEffect, useMemo, useRef, useCallback } from "react"
import { useParams } from "react-router-dom"
import { loadStripe, Stripe, StripeEmbeddedCheckout } from "@stripe/stripe-js"

import { PaygateProps } from "../paygate"
import timerBox from "./assets/checkout-timer.svg"
import border from "./assets/checkout-border.svg"
import discountBox from "./assets/original-price-box.svg"
import { usePaygate2ScreenSharedState } from "./shared-state"
import { planNames } from "./paygate-2screen-paywall"
import { soul } from "lib/soul"
import { generateAppsFlyerLink } from "lib/common"
import { StripeSuccessHandler } from "../stripe-success-handler"
import { replaceDotWithComma } from "./plans"

type Props = Omit<PaygateProps, "type">

export function Paygate2ScreenCheckout(props: Props) {
  const { onPaymentError, isActive, surveyId } = props
  const isStripeSuccess = !!new URL(location.href).searchParams.get(
    "stripe_success",
  )

  const { formattedCountdown, selectedPlan, originalPrices, apiPlans } =
    usePaygate2ScreenSharedState(surveyId, planNames[1])

  const originalPrice = originalPrices[planNames.indexOf(selectedPlan)] ?? 0
  const priceAfterDiscount =
    apiPlans.find((plan) => plan.name === selectedPlan)?.price ?? 0
  const discount = Math.round((1 - priceAfterDiscount / originalPrice) * 100)

  const stripeRef = useRef<Stripe | null>(null)
  const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null)
  const { locale } = useParams<{ locale?: string }>()
  const email = useMemo(
    () =>
      typeof window !== "undefined"
        ? window.localStorage.getItem("user_email")
        : null,
    [isActive],
  )

  useEffect(() => {
    if (!isActive) return

    window.scrollTo({
      top: 0,
      behavior: "auto",
    })
  }, [isActive])

  const loadStripeObj = useCallback(async () => {
    if (stripeRef.current) return stripeRef.current

    const key = process.env.REACT_APP_STRIPE_KEY ?? ""
    if (!key) {
      onPaymentError("Missing Stripe public key")
      return null
    }

    const stripe = await loadStripe(key)
    stripeRef.current = stripe
    return stripe
  }, [onPaymentError])

  const fetchStripeClientSecret = useCallback(
    async (bundle: string): Promise<string> => {
      if (!email) {
        throw new Error("Missing email")
      }

      const returnUrl = new URL(window.location.href)
      returnUrl.searchParams.set("stripe_success", "1")

      try {
        const { client_secret, funnel_user_id } =
          await soul.purchases.getStripeEmbedded({
            bundle,
            return_url: `${returnUrl.href}&session_id={CHECKOUT_SESSION_ID}`,
            email,
            lang_code: locale,
            app_flyer_link: `${generateAppsFlyerLink(
              surveyId,
            )}&deep_link_value=funnel_authorization`,
            payment_request_host: window.location.origin,
          })

        window.localStorage.setItem("funnel_user_id", funnel_user_id)

        return client_secret
      } catch (err) {
        onPaymentError?.("stripe get client secret failed")
        throw err
      }
    },
    [email, locale, onPaymentError, surveyId],
  )

  const mountStripe = useCallback(
    async (bundle: string) => {
      const stripe = await loadStripeObj()
      if (!stripe || !email) return

      if (checkoutRef.current) {
        checkoutRef.current.destroy()
        checkoutRef.current = null
      }

      try {
        const checkout = await stripe.initEmbeddedCheckout({
          fetchClientSecret: () => fetchStripeClientSecret(bundle),
        })

        checkout.mount("#stripe_checkout")
        checkoutRef.current = checkout
      } catch (e) {
        onPaymentError?.(`stripe mount failed with bundle ${bundle}`)
      }
    },
    [email, fetchStripeClientSecret, loadStripeObj, onPaymentError],
  )

  useEffect(() => {
    if (!isActive || !email) return
    if (checkoutRef.current) return
    if (isStripeSuccess) return

    let cancelled = false

    const init = async () => {
      try {
        if (!cancelled) {
          await mountStripe(selectedPlan)
        }
      } catch {}
    }

    init()

    return () => {
      cancelled = true
      if (checkoutRef.current) {
        checkoutRef.current.destroy()
        checkoutRef.current = null
      }
    }
  }, [isActive, email, mountStripe, selectedPlan, isStripeSuccess])

  if (isStripeSuccess && isActive) {
    return <StripeSuccessHandler goNext={props.onSuccess} />
  }

  return (
    <div className="w-full min-h-dvh flex flex-col">
      <div className="bg-black px-8 pt-6 pb-12 text-white">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-quirk text-2xl tracking-[0.7px] -rotate-[5deg]">
            {discount}% off ends in
          </h1>
          <div className="mb-4 relative shrink-0">
            <img src={timerBox} width={119} height={49} alt="" />
            <div className="absolute rotate-[5deg] font-quirk uppercase text-[18px] leading-[28px] tracking-[0.4px] bottom-[10px] left-[20px]">
              <span className="inline-block w-[42px]">
                {formattedCountdown}
              </span>{" "}
              left
            </div>
          </div>
        </div>
        <h2 className="text-lg uppercase tracking-[1.5px] -rotate-[2deg]">
          Join over 1,897,667 people who already use Pure
        </h2>
      </div>
      <img src={border} className="w-full h-[28px] -mt-[26px]" alt="" />
      <div
        className="bg-no-repeat bg-[length:100%_100%] bg-center py-4 px-6 my-2 mx-5 -rotate-[1deg]"
        style={{ backgroundImage: `url(${discountBox})` }}
      >
        <div className="font-sans-bold text-[16px] leading-[28px] uppercase">
          Original price ${replaceDotWithComma(originalPrice)}
        </div>
        <div className="text-base font-sans-bold text-violet-190">
          {discount}% introductory offer discount $
          {replaceDotWithComma((originalPrice - priceAfterDiscount).toFixed(2))}
        </div>
      </div>
      <div className="bg-white grow" id="stripe_checkout" />
    </div>
  )
}
